import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Phone, ShieldCheck } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { LANGUAGES } from "@/lib/constants";
import { api } from "@/lib/api";
import { ParishSiteBadge } from "@/components/ParishSiteBadge";

export const LOGO = `${process.env.PUBLIC_URL || ""}/sfa-logo.webp`;

export function usePublicSettings() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/public/settings").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  return s;
}

export const ScamNotice = ({ compact }) => {
  const s = usePublicSettings();
  return (
    <div data-testid="scam-notice" className="rounded-2xl border border-[#C5D9C9] bg-[#EBF2EC] p-4 sm:p-5 text-[15px] text-[#22402A]">
      <p className="font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Protect yourself from scams</p>
      <ul className={`mt-2 list-disc pl-5 space-y-1 ${compact ? "text-sm" : ""}`}>
        <li>MABUHAY CARE is free. The parish will <b>never</b> ask you for money, a processing fee, load, an OTP code, or bank or GCash details.</li>
        <li>Parish workers identify themselves by name and ministry. If unsure, hang up and call the parish office{s.office_phone ? <> at <a href={`tel:${s.office_phone.replace(/[^\d+]/g, "")}`} className="font-bold underline" data-testid="office-phone-link">{s.office_phone}</a></> : ""} to confirm.</li>
        <li>Never share your PIN. Parish staff will not ask for it.</li>
      </ul>
    </div>
  );
};

export const CrossMark = ({ className = "" }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <rect x="17" y="4" width="6" height="32" rx="1.5" fill="currentColor" />
    <rect x="8" y="12" width="24" height="6" rx="1.5" fill="currentColor" />
  </svg>
);

export const EmergencyNotice = () => {
  const { t } = useLang();
  return (
    <div data-testid="emergency-notice" role="note" className="bg-[#FEF3C7] border-b border-[#F59E0B] text-[#78350F]">
      <div className="max-w-6xl mx-auto px-5 py-2.5 flex items-start gap-3 text-sm sm:text-[15px]">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p>{t("notice")} <a href="tel:911" className="font-bold underline inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />911</a></p>
      </div>
    </div>
  );
};

export const LanguageSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <select data-testid="language-switcher" aria-label="Language" value={lang} onChange={(e) => setLang(e.target.value)}
      className="h-11 rounded-full border border-[#E7DFD5] bg-white px-3 text-sm font-semibold text-[#4A2E1A] focus:outline-none focus:ring-2 focus:ring-[#C59B27]">
      {Object.entries(LANGUAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
};

export default function PublicLayout({ children }) {
  const { t, lang } = useLang();
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <EmergencyNotice />
      <header className="border-b border-[#E7DFD5] bg-[#FDFBF7]/90 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-3 min-w-0">
            <img src={LOGO} alt="Saint Francis of Assisi Parish logo" data-testid="header-logo" width="48" height="47" className="w-12 h-12 object-contain shrink-0" />
            <span className="min-w-0">
              <span className="block font-serif-head font-black text-[#4A2E1A] tracking-wide leading-none">MABUHAY CARE</span>
              <span className="block text-xs text-[#605349] truncate">Saint Francis of Assisi Parish</span>
            </span>
          </Link>
          <LanguageSwitch />
        </div>
      </header>
      {lang !== "en" && (
        <div data-testid="draft-translation-banner" className="bg-[#FDF7E7] text-[#7A5A10] text-xs text-center py-1.5 px-4 border-b border-[#E9D49A]">{t("draft")}</div>
      )}
      <main className="flex-1">{children}</main>
      <footer className="border-t border-[#E7DFD5] bg-[#F4EBE4]/60">
        <div className="max-w-6xl mx-auto px-5 py-8 text-sm text-[#605349] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold text-[#4A2E1A]">Saint Francis of Assisi Parish</p>
            <p>Barangay Mabuhay, General Santos City · Diocese of Marbel</p>
          </div>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="underline underline-offset-4 hover:text-[#4A2E1A]">{t("privacy")}</Link>
            <a href="https://mabuhayparish.org" data-testid="footer-parish-website-link" className="underline underline-offset-4 hover:text-[#4A2E1A]">mabuhayparish.org</a>
          </div>
        </div>
      </footer>
      <ParishSiteBadge />
    </div>
  );
}
